import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import VehicleCard from '@/components/VehicleCard';
import HappyCustomersGallery from '@/components/HappyCustomersGallery';
import { Car, Shield, DollarSign, Clock, ChevronRight, Star, Users, Award } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const HomePage = () => {
  const seoData = useSEO('home', 'Quality Used Cars | We Have Your Car');
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedVehicles();
    fetchReviews();
    fetchSiteSettings();
  }, []);

  const fetchFeaturedVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?active_only=true`);
      const allVehicles = response.data;
      
      // Get featured vehicles
      const featured = allVehicles.filter(v => v.is_featured);
      const nonFeatured = allVehicles.filter(v => !v.is_featured);
      
      // Shuffle function
      const shuffle = (arr) => {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };
      
      let displayVehicles;
      if (featured.length >= 6) {
        // 6 or more featured: randomize and pick 6 from featured only
        displayVehicles = shuffle(featured).slice(0, 6);
      } else {
        // Less than 6 featured: show all featured + fill rest with random non-featured
        const remainingSlots = 6 - featured.length;
        const randomNonFeatured = shuffle(nonFeatured).slice(0, remainingSlots);
        displayVehicles = [...featured, ...randomNonFeatured];
      }
      
      setFeaturedVehicles(displayVehicles);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const googleRating = siteSettings?.google_rating || 4.8;
  const reviewCount = siteSettings?.google_review_count || 47;
  const statHappyCustomers = siteSettings?.stat_happy_customers || '500+';
  const statVehiclesSold = siteSettings?.stat_vehicles_sold || '100+';
  const statYearsInBusiness = siteSettings?.stat_years_in_business || '5';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&h=1080&fit=crop)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Exo_2'] leading-tight">
              Find Your Perfect <span className="gradient-text">Ride</span> Today
            </h1>
            <p className="text-lg text-[#a8b8d0] mb-8">
              Quality used vehicles with flexible financing options. We make car buying easy in Austin, TX.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/inventory"
                className="btn-accent inline-flex items-center justify-center gap-2"
                data-testid="browse-inventory-btn"
              >
                Browse Inventory
                <ChevronRight size={20} />
              </Link>
              <Link
                to="/credit-application"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="apply-financing-btn"
              >
                Apply for Financing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#132238]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-xl bg-[#1a2d47]/50 border border-[#1a2d47]">
              <div className="bg-[#ff6b35]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-[#ff6b35]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Quality Vehicles</h3>
              <p className="text-[#a8b8d0] text-sm">Every car is inspected and ready to drive</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-[#1a2d47]/50 border border-[#1a2d47]">
              <div className="bg-[#ff6b35]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-[#ff6b35]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Flexible Financing</h3>
              <p className="text-[#a8b8d0] text-sm">Options for all credit situations</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-[#1a2d47]/50 border border-[#1a2d47]">
              <div className="bg-[#ff6b35]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#ff6b35]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No Hidden Fees</h3>
              <p className="text-[#a8b8d0] text-sm">Transparent pricing on every vehicle</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-[#1a2d47]/50 border border-[#1a2d47]">
              <div className="bg-[#ff6b35]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#ff6b35]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Fast Approval</h3>
              <p className="text-[#a8b8d0] text-sm">Get approved in minutes, not days</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="py-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Exo_2']">
              Featured <span className="gradient-text">Vehicles</span>
            </h2>
            <p className="text-[#a8b8d0] max-w-2xl mx-auto">
              Check out our latest arrivals and best deals on quality used vehicles
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#132238] rounded-xl h-80 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredVehicles.map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} />
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link
              to="/inventory"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="view-all-inventory-btn"
            >
              View All Inventory
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1a4d8c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-white mb-2">{statHappyCustomers}</p>
              <p className="text-white/70">Happy Customers</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">{statVehiclesSold}</p>
              <p className="text-white/70">Vehicles Sold</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">{statYearsInBusiness}</p>
              <p className="text-white/70">Years in Business</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 mb-2">
                <p className="text-4xl font-bold text-white">{googleRating}</p>
                <Star className="w-8 h-8 text-[#ffc107] fill-[#ffc107]" />
              </div>
              <p className="text-white/70">Google Rating ({reviewCount} reviews)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Happy Customers Gallery */}
      <HappyCustomersGallery />

      {/* Customer Reviews */}
      {reviews.length > 0 && (
        <section className="py-16 bg-[#132238]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Exo_2']">
                What Our <span className="gradient-text">Customers Say</span>
              </h2>
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="flex">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={24} 
                      className={i <= Math.round(googleRating) ? 'text-[#ffc107] fill-[#ffc107]' : 'text-gray-500'} />
                  ))}
                </div>
                <span className="text-white font-medium text-lg">{googleRating}</span>
                <span className="text-[#a8b8d0]">({reviewCount} Google reviews)</span>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div key={review.id} className="bg-[#1a2d47] rounded-xl p-6 border border-[#1a4d8c]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={16} 
                          className={i <= review.rating ? 'text-[#ffc107] fill-[#ffc107]' : 'text-gray-500'} />
                      ))}
                    </div>
                    <span className="text-[#a8b8d0] text-xs capitalize">{review.source}</span>
                  </div>
                  <p className="text-[#a8b8d0] text-sm mb-4 line-clamp-4">{review.text}</p>
                  <p className="text-white font-medium">{review.author_name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Exo_2']">
                Why Choose <span className="gradient-text">We Have Your Car?</span>
              </h2>
              <p className="text-[#a8b8d0] mb-6">
                At We Have Your Car, we believe everyone deserves a reliable vehicle. Our team is dedicated to helping you find the perfect car at a price that fits your budget. With flexible financing options and a commitment to customer satisfaction, we make car buying a breeze.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="bg-[#22c55e]/20 p-1 rounded-full mt-1">
                    <Star className="w-4 h-4 text-[#22c55e]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Quality Assurance</h4>
                    <p className="text-[#a8b8d0] text-sm">Every vehicle undergoes thorough inspection</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-[#22c55e]/20 p-1 rounded-full mt-1">
                    <Users className="w-4 h-4 text-[#22c55e]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Customer First</h4>
                    <p className="text-[#a8b8d0] text-sm">We work with your budget and credit situation</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-[#22c55e]/20 p-1 rounded-full mt-1">
                    <Award className="w-4 h-4 text-[#22c55e]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Trusted Dealer</h4>
                    <p className="text-[#a8b8d0] text-sm">Serving Austin community for years</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=600&h=400&fit=crop"
                alt="Car dealership"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#ff6b35] p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold text-white">{statYearsInBusiness}+</p>
                <p className="text-white/80 text-sm">Years Serving Austin</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1a4d8c] to-[#0f3460]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Exo_2']">
            Ready to Find Your Next Car?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Apply for financing today and get pre-approved in minutes!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/credit-application"
              className="btn-accent inline-flex items-center justify-center gap-2"
              data-testid="cta-apply-btn"
            >
              Apply for Credit
              <ChevronRight size={20} />
            </Link>
            <a
              href="tel:5126660016"
              className="bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              Call (512) 666-0016
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
